<script lang="ts" setup>
import {
  reactive,
  ref,
  useI18n,
  useToast,
  computed,
  onUnmounted,
  useAuth,
  watch,
  useDialog,
} from "#imports";
import type { AuthSteps } from "./MPAuth.vue";
import MPMessage from "~/components/widgets/MPMessage.vue";
import MPInput from "~/components/forms/MPInput.vue";
import MPButton from "~/components/forms/MPButton.vue";
import MPImg from "~/components/widgets/MPImg.vue";
import { isEmail, isRequired } from "~/components/forms/validation";
const { t } = useI18n();

const emit = defineEmits<{
  toggleAuthStep: [value: AuthSteps];
}>();

// Login form
const loginModel = reactive({
  email: "",
});

const { closeDialog } = useDialog();

const magicLinkSent = ref(false);
const countdown = ref(0);
let countdownInterval: NodeJS.Timeout | null = null;

const magicLinkButtonText = computed(() => {
  if (countdown.value > 0) {
    return `Send new link in ${countdown.value} seconds`;
  }
  return "Login with Magic Link";
});

const startCountdown = () => {
  countdown.value = 60;
  const intervalId = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      clearInterval(intervalId);
      countdownInterval = null;
    }
  }, 1000);
  countdownInterval = intervalId;
};

onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }
});

const { showError, showSuccess } = useToast();
const authStore = useAuth();

const loginFailed = ref(false);
const isLoading = ref(false);

const handleMagicLink = async () => {
  isLoading.value = true;
  try {
    await authStore.signInWithMagicLink({ email: loginModel.email });
    magicLinkSent.value = true;
    startCountdown();
    showSuccess("Magic link sent");
  } catch (e) {
    loginFailed.value = true;
    showError("Failed to send magic link", e);
  } finally {
    isLoading.value = false;
  }
};

// it's possible we login in another window, but this window will also login, go supabase!
watch(
  () => authStore.isLoggedIn,
  () => {
    if (authStore.isLoggedIn) {
      closeDialog();
    }
  },
);
</script>

<template>
  <MPMessage v-if="loginFailed" version="error">
    Failed to send magic link
  </MPMessage>
  <div class="title">
    <h3>Welcome <span>Back</span></h3>
    <p>Please enter your details</p>
  </div>
  <form class="login" @submit.prevent="handleMagicLink">
    <div>
      <MPInput
        v-model="loginModel.email"
        name="email"
        :disabled="isLoading || countdown > 0"
        type="email"
        :placeholder="t('auth.login.email')"
        class="input-email"
        :validate="(v) => isRequired(v) || isEmail(v)"
      />
    </div>

    <MPButton
      :label="magicLinkButtonText"
      :disabled="isLoading || countdown > 0"
      type="submit"
    />

    <MPMessage v-if="magicLinkSent" version="success">
      Magic link sent! Please check your email.
    </MPMessage>

    <div class="divider">
      <hr />
      <small>or</small>
      <hr />
    </div>

    <MPButton
      label="Login with Password"
      :disabled="isLoading"
      version="secondary"
      @click="emit('toggleAuthStep', 'login')"
    />

    <div class="social-oauth">
      <MPImg
        src="/media/images/google-social-auth.svg"
        @click="authStore.signInWithOAuth('google')"
      />
    </div>
    <small class="signup-link" @click="emit('toggleAuthStep', 'signup')">
      Don't have an account yet? <span>Join</span>
    </small>
  </form>
</template>

<style lang="postcss" scoped>
.login {
  display: grid;
  gap: 1rem;
  padding: 2rem;
}

.title {
  color: var(--color-slate-500);
  h3 {
    margin: 0;
    margin-top: 2rem;
    margin-bottom: 0.5rem;
    span {
      color: var(--color-rose-400);
    }
  }
  p {
    margin: 0;
    padding-inline: 4rem;
    margin-top: 1rem;
    line-height: 1.2 !important;
  }
}

.divider {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
  color: var(--color-slate-500);
}

.social-oauth {
  display: flex;
  gap: 0.5rem;
  margin: auto;
  img {
    cursor: pointer;
  }
}

.signup-link {
  cursor: pointer;
  span {
    color: var(--color-rose-400);
  }
}
</style>
